import React, { useState, useEffect } from "react";
import { Typography, Tabs, message } from "antd";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { trackFeatureUsage, captureError } from "../lib/posthog";
import TariffManagementTab from "../components/pricing/tariffs/TariffManagementTab";
import TariffTimelineTab from "../components/pricing/tariffs/TariffTimelineTab";
import { AdminNavBar } from "../components/AdminNavBar";
import {
  Tariff,
  TransportType,
  Port,
  TariffBracket,
  ContainerSize,
  BookingType,
  TariffFilter,
} from "../components/pricing/tariffs/utils/types";
import dayjs from "dayjs";

const { Title } = Typography;

const TariffsAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("management");
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [transportTypes, setTransportTypes] = useState<TransportType[]>([]);
  const [originPorts, setOriginPorts] = useState<Port[]>([]);
  const [destinationPorts, setDestinationPorts] = useState<Port[]>([]);
  const [tariffBrackets, setTariffBrackets] = useState<TariffBracket[]>([]);
  const [containerSizes, setContainerSizes] = useState<ContainerSize[]>([]);
  const [bookingTypes, setBookingTypes] = useState<BookingType[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch tariffs with related data
  const fetchTariffs = async (filters: TariffFilter = {}) => {
    try {
      setLoading(true);
      let query = supabase.from("tariffs").select(
        `
          *,
          transport_type_data:booking_transport_types(*),
          origin_port_data:booking_origin_port(*),
          destination_port_data:booking_destination_port(*),
          tariff_bracket_data:tariff_brackets(*),
          container_size_data:container_size(*),
          bookings_type_data:bookings_type(*)
        `
      );

      // Apply any filters provided
      if (filters) {
        // Filter by transport type
        if (filters.transportType) {
          query = query.eq("transport_type", filters.transportType);
        }

        // Filter by origin port
        if (filters.originPort) {
          query = query.eq("origin_port", filters.originPort);
        }

        // Filter by destination port
        if (filters.destinationPort) {
          query = query.eq("destination_port", filters.destinationPort);
        }

        // Filter by booking type
        if (filters.bookingType) {
          query = query.eq("bookings_type", filters.bookingType);
        }

        // Filter by container size
        if (filters.containerSize) {
          query = query.eq("container_size", filters.containerSize);
        }

        // Filter by date range (if both dates are provided)
        if (
          filters.dateRange &&
          filters.dateRange.length === 2 &&
          filters.dateRange[0] &&
          filters.dateRange[1]
        ) {
          const [startDate, endDate] = filters.dateRange;

          // Find tariffs that overlap with this range
          // A tariff overlaps if:
          // 1. Tariff start date is within the range OR
          // 2. Tariff end date is within the range OR
          // 3. Tariff spans the entire range
          query = query.or(
            `and(from.gte.${startDate.format(
              "YYYY-MM-DD"
            )},from.lte.${endDate.format("YYYY-MM-DD")}),` +
              `and(to.gte.${startDate.format(
                "YYYY-MM-DD"
              )},to.lte.${endDate.format("YYYY-MM-DD")}),` +
              `and(from.lt.${startDate.format(
                "YYYY-MM-DD"
              )},to.gt.${endDate.format("YYYY-MM-DD")})`
          );
        }
        // Filter by start date only (for "Active Now" filter)
        else if (
          filters.dateRange &&
          filters.dateRange.length >= 1 &&
          filters.dateRange[0]
        ) {
          const currentDate = filters.dateRange[0].format("YYYY-MM-DD");

          // Find tariffs that are active now:
          // (from <= currentDate AND (to IS NULL OR to >= currentDate))
          // This matches the URL pattern seen in the user's issue
          query = query.or(
            `and(from.lte.${currentDate},or(to.is.null,to.gte.${currentDate}))`
          );
        }
      }

      // Add ordering last
      query = query.order("from", { ascending: false });

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      setTariffs(data || []);
    } catch (error) {
      console.error("Error fetching tariffs:", error);
      message.error("Failed to load tariffs");
      captureError(error, {
        source: "TariffsAdmin",
        action: "fetchTariffs",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch transport types
  const fetchTransportTypes = async () => {
    try {
      const { data, error } = await supabase
        .from("booking_transport_types")
        .select("*")
        .order("name");

      if (error) {
        throw error;
      }

      setTransportTypes(data || []);
    } catch (error) {
      console.error("Error fetching transport types:", error);
      message.error("Failed to load transport types");
      captureError(error, {
        source: "TariffsAdmin",
        action: "fetchTransportTypes",
      });
    }
  };

  // Fetch origin ports
  const fetchOriginPorts = async () => {
    try {
      const { data, error } = await supabase
        .from("booking_origin_port")
        .select("*")
        .order("name");

      if (error) {
        throw error;
      }

      setOriginPorts(data || []);
    } catch (error) {
      console.error("Error fetching origin ports:", error);
      message.error("Failed to load origin ports");
      captureError(error, {
        source: "TariffsAdmin",
        action: "fetchOriginPorts",
      });
    }
  };

  // Fetch destination ports
  const fetchDestinationPorts = async () => {
    try {
      const { data, error } = await supabase
        .from("booking_destination_port")
        .select("*")
        .order("name");

      if (error) {
        throw error;
      }

      setDestinationPorts(data || []);
    } catch (error) {
      console.error("Error fetching destination ports:", error);
      message.error("Failed to load destination ports");
      captureError(error, {
        source: "TariffsAdmin",
        action: "fetchDestinationPorts",
      });
    }
  };

  // Fetch tariff brackets
  const fetchTariffBrackets = async () => {
    try {
      const { data, error } = await supabase
        .from("tariff_brackets")
        .select("*")
        .order("from", { ascending: true });

      if (error) {
        throw error;
      }

      setTariffBrackets(data || []);
    } catch (error) {
      console.error("Error fetching tariff brackets:", error);
      message.error("Failed to load tariff brackets");
      captureError(error, {
        source: "TariffsAdmin",
        action: "fetchTariffBrackets",
      });
    }
  };

  // Fetch container sizes
  const fetchContainerSizes = async () => {
    try {
      const { data, error } = await supabase
        .from("container_size")
        .select("*")
        .order("name");

      if (error) {
        throw error;
      }

      setContainerSizes(data || []);
    } catch (error) {
      console.error("Error fetching container sizes:", error);
      message.error("Failed to load container sizes");
      captureError(error, {
        source: "TariffsAdmin",
        action: "fetchContainerSizes",
      });
    }
  };

  // Fetch booking types
  const fetchBookingTypes = async () => {
    try {
      const { data, error } = await supabase
        .from("bookings_type")
        .select("*")
        .order("name");

      if (error) {
        throw error;
      }

      setBookingTypes(data || []);
    } catch (error) {
      console.error("Error fetching booking types:", error);
      message.error("Failed to load booking types");
      captureError(error, {
        source: "TariffsAdmin",
        action: "fetchBookingTypes",
      });
    }
  };

  useEffect(() => {
    // Track page view
    trackFeatureUsage("tariffs_admin");

    // Fetch all necessary data
    fetchTariffs();
    fetchTransportTypes();
    fetchOriginPorts();
    fetchDestinationPorts();
    fetchTariffBrackets();
    fetchContainerSizes();
    fetchBookingTypes();

    return () => {
      // Cleanup if needed
    };
  }, []);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    trackFeatureUsage(`tariffs_admin_${key}_tab`);
  };

  try {
    const items = [
      {
        key: "management",
        label: "Tariff Management",
        children: (
          <TariffManagementTab
            tariffs={tariffs}
            transportTypes={transportTypes}
            originPorts={originPorts}
            destinationPorts={destinationPorts}
            tariffBrackets={tariffBrackets}
            containerSizes={containerSizes}
            bookingTypes={bookingTypes}
            loading={loading}
            fetchTariffs={fetchTariffs}
          />
        ),
      },
      {
        key: "timeline",
        label: "Timeline View",
        children: (
          <TariffTimelineTab
            tariffs={tariffs}
            transportTypes={transportTypes}
            originPorts={originPorts}
            destinationPorts={destinationPorts}
            tariffBrackets={tariffBrackets}
            containerSizes={containerSizes}
            bookingTypes={bookingTypes}
            loading={loading}
            fetchTariffs={fetchTariffs}
          />
        ),
      },
    ];

    return (
      <div style={{ padding: 24 }}>
        <AdminNavBar />

        <Title level={2}>Pricing Tariffs Administration</Title>

        <Tabs activeKey={activeTab} onChange={handleTabChange} items={items} />
      </div>
    );
  } catch (error) {
    console.error("Error rendering Tariffs Admin:", error);
    captureError(error, { source: "TariffsAdmin", action: "render" });

    return (
      <div
        style={{
          padding: "2rem",
          background: "#fff1f0",
          border: "1px solid #ffa39e",
        }}
      >
        <h2>Error Rendering Tariffs Admin</h2>
        <p>An error occurred while rendering the Tariffs Admin page:</p>
        <pre
          style={{ background: "#f5f5f5", padding: "1rem", overflow: "auto" }}
        >
          {error instanceof Error ? error.toString() : String(error)}
          {error instanceof Error ? error.stack : ""}
        </pre>
      </div>
    );
  }
};

export default TariffsAdmin;
